import { lazy } from 'react';

/**
 * `React.lazy`, for a page (or dialog) whose code is a separate file — which
 * a deploy RENAMES. A tab opened before a hosting deploy still runs the old
 * build; the first time it opens a page it has not loaded yet, it asks for
 * the old file name. Hosting answers index.html for a file that is gone (the
 * SPA rewrite), the chunk never registers, and the import rejects. With
 * nothing catching that, React unmounted the whole app and left the olive
 * page background — the "green blank screen" after Save as estimate on
 * 2026-09-28.
 *
 * So a missing chunk reloads the page ONCE, which fetches the new build
 * with its new file names, and waits for the reload rather than rendering an
 * error in between. The reload is marked in sessionStorage: a second failure
 * in the same session is not a stale tab, and goes to the error boundary
 * (`components/AppErrorBoundary`) instead of reloading forever. A page that
 * loads clears the mark, so the next deploy can reload again. With no
 * storage to hold the mark (a private window) it never reloads — it could
 * not tell a loop.
 */

const MARK = 'housfully:chunk-reload';

/** Whether an import failed because the file is not there to load. */
export const isChunkLoadError = (err) =>
  Boolean(err) &&
  (err.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(err.message ?? '') ||
    // Chrome and Firefox for a native dynamic import; Safari's own words
    /Failed to fetch dynamically imported module/i.test(err.message ?? '') ||
    /Importing a module script failed/i.test(err.message ?? ''));

const safe = (fn) => {
  try {
    return { ok: true, value: fn() };
  } catch (err) {
    return { ok: false };
  }
};

const sessionStore = () =>
  typeof window !== 'undefined' ? window.sessionStorage : undefined;

/**
 * Load a module; on a missing chunk, reload the page once (see above).
 * storage / reload are injectable for tests.
 */
export const loadWithReload = (
  load,
  { storage = sessionStore(), reload = () => window.location.reload() } = {}
) =>
  load().then(
    (mod) => {
      safe(() => storage?.removeItem(MARK));
      return mod;
    },
    (err) => {
      if (!isChunkLoadError(err) || !storage) throw err;
      const seen = safe(() => storage.getItem(MARK));
      if (!seen.ok || seen.value) throw err;
      const marked = safe(() => storage.setItem(MARK, String(Date.now())));
      if (!marked.ok) throw err;
      reload();
      // the page is going away: render nothing new in the meantime
      return new Promise(() => {});
    }
  );

/** `React.lazy(load)` that reloads once when the page's file is gone. */
export const lazyWithReload = (load) => lazy(() => loadWithReload(load));

export default lazyWithReload;
